<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue"
import {useRoute} from "vue-router"
import { storeToRefs } from "pinia"
import { useUsersStore, useAppModeStore } from "@/stores"
import { shouldGreet, markGreeted } from "@/utils/launcherSession"
import DiscoverContent from "@core/components/DiscoverContent.vue"
import RmAiLauncherSheet from "@core/components/RmAiLauncherSheet.vue"

const route = useRoute()
const router = useRouter()
const pathSuffix = route.params.all

//split up into array based on /
const pathSuffixArray = pathSuffix.split('/')

// RM AI launcher: greets a Platinum member with the RM AI screen pulled up
// ~3/4 over Discover, but only ONCE per session — i.e. on their first landing
// after logging in / starting a new session. It does NOT reopen on every visit
// to Discover; mid-session, members reach RM AI via the nav-bar "RM AI" item.
// Gated to member mode only (RM AI is member-mode only), so it's hidden in
// "Restaurant Account" mode. Everyone else keeps the standard Discover landing.
const usersStore = useUsersStore()
const appModeStore = useAppModeStore()
const { myUser } = storeToRefs(usersStore)
const { mode: appMode, hydrated: appModeHydrated } = storeToRefs(appModeStore)

const isPlatinumMember = computed(() => !!(myUser.value?.activeSubscription && myUser.value?.isPlatinum))

// Wait for app-mode hydration so we don't flash the launcher for a member who
// is actually in Restaurant Account mode (the store defaults to 'member').
const eligibleForLauncher = computed(() =>
  appModeHydrated.value && isPlatinumMember.value && appMode.value === 'member')

const launcherOpen = ref(false)

function maybeOpenLauncher() {
  if (!eligibleForLauncher.value) return

  // shouldGreet() = not greeted this session yet, OR returning after idle.
  if (!shouldGreet()) return
  markGreeted()
  launcherOpen.value = true
}

// `immediate` covers the already-eligible case; the watch covers late-arriving
// myUser / app-mode hydration when Discover mounts before they resolve.
watch(eligibleForLauncher, value => {
  if (value) maybeOpenLauncher()
}, { immediate: true })

// Returning to the tab after being away: if they were idle past the threshold
// while sitting on Discover, re-greet on refocus.
function handleVisibilityChange() {
  if (document.visibilityState === 'visible') maybeOpenLauncher()
}

onMounted(() => document.addEventListener('visibilitychange', handleVisibilityChange))
onBeforeUnmount(() => document.removeEventListener('visibilitychange', handleVisibilityChange))

//setup the page based on the end of the url
// onMounted(() => {
//   if (pathSuffixArray[0]) {
//     //take pathSuffixArray[0] and remove the - and replace with a space, and capitalize the first letter of each word
//     let modifiedPath = pathSuffixArray[0].split('-').map((word) => {
//       return word.charAt(0).toUpperCase() + word.slice(1);
//     }).join(' ');
//
//     selectedType.value = modifiedPath;
//     if (pathSuffixArray[1]) {
//       //take pathSuffixArray[1] and remove the - and replace with a command and space, and capitalize the first letter of each word and capitalize both letters if it ends with e state abbreviation
//       let modifiedPath = pathSuffixArray[1].split('-').map((word) => {
//         if (word.length == 2) {
//           return word.toUpperCase();
//         }
//         return word.split(' ').map(function(singleWord) {
//           return singleWord.charAt(0).toUpperCase() + singleWord.slice(1);
//         }).join(' ');
//       }).join(', ');
//       selectedMarketFavorties.value = modifiedPath;
//     }
//   }
// });
</script>

<template>
  <DiscoverContent />
  <RmAiLauncherSheet
    v-if="isPlatinumMember"
    v-model="launcherOpen"
  />
</template>



<route lang="yaml">
meta:
  layout: website
  action: read
  subject: Auth
</route>
